import { useState, useEffect, useCallback } from 'react';
import type {
  Integration,
  IntegrationType,
  CreateIntegrationDto,
  UpdateIntegrationDto,
  IntegrationTestResult,
} from '@yawi/types';
import { useServices, useToast } from '@/app/providers';

interface UseIntegrationsReturn {
  integrations: Integration[];
  isLoading: boolean;
  isSaving: boolean;
  isTesting: boolean;
  selectedType: IntegrationType | null;
  setSelectedType: (type: IntegrationType | null) => void;
  createIntegration: (data: CreateIntegrationDto) => Promise<Integration>;
  updateIntegration: (id: string, data: UpdateIntegrationDto) => Promise<void>;
  deleteIntegration: (id: string) => Promise<void>;
  testIntegration: (id: string) => Promise<IntegrationTestResult>;
  enableIntegration: (id: string) => Promise<void>;
  disableIntegration: (id: string) => Promise<void>;
  refresh: () => void;
  getIntegrationById: (id: string) => Promise<Integration>;
}

// Mock data for development
const mockIntegrations: Integration[] = [
  {
    id: '550e8400-e29b-41d4-a716-446655440000',
    type: 'EMAIL',
    provider: 'SENDGRID',
    name: 'Email Principal - SendGrid',
    status: 'ENABLED',
    priority: 0,
    lastError: null,
    lastUsedAt: new Date().toISOString(),
    errorCount: 0,
    createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: '550e8400-e29b-41d4-a716-446655440001',
    type: 'EMAIL',
    provider: 'SMTP',
    name: 'Email Backup - SMTP',
    status: 'ENABLED',
    priority: 1,
    lastError: null,
    lastUsedAt: null,
    errorCount: 0,
    createdAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
  },
];

export function useIntegrations(): UseIntegrationsReturn {
  const { integrationRepository } = useServices();
  const { showToast } = useToast();
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [selectedType, setSelectedType] = useState<IntegrationType | null>(null);

  const fetchIntegrations = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await integrationRepository.getIntegrations(selectedType ?? undefined);
      setIntegrations(data);
    } catch (err) {
      console.error('Failed to fetch integrations:', err);
      // Use mock data for development
      let filtered = mockIntegrations;
      if (selectedType) {
        filtered = mockIntegrations.filter(i => i.type === selectedType);
      }
      setIntegrations(filtered);
    } finally {
      setIsLoading(false);
    }
  }, [integrationRepository, selectedType]);

  useEffect(() => {
    fetchIntegrations();
  }, [fetchIntegrations]);

  const getIntegrationById = useCallback(async (id: string): Promise<Integration> => {
    try {
      return await integrationRepository.getIntegrationById(id);
    } catch (err) {
      console.error('Failed to get integration:', err);
      const found = integrations.find(i => i.id === id);
      if (found) return found;
      throw err;
    }
  }, [integrationRepository, integrations]);

  const createIntegration = useCallback(async (data: CreateIntegrationDto): Promise<Integration> => {
    setIsSaving(true);
    try {
      const integration = await integrationRepository.createIntegration(data);
      showToast('success', 'Integración creada exitosamente');
      fetchIntegrations();
      return integration;
    } catch (err) {
      console.error('Failed to create integration:', err);
      const message = err instanceof Error ? err.message : 'Error al crear integración';
      showToast('error', message);
      // Return mock for development
      const mockIntegration: Integration = {
        id: `mock-${Date.now()}`,
        type: data.type,
        provider: data.provider,
        name: data.name,
        status: 'DISABLED',
        priority: data.priority ?? 0,
        lastError: null,
        lastUsedAt: null,
        errorCount: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setIntegrations(prev => [...prev, mockIntegration]);
      return mockIntegration;
    } finally {
      setIsSaving(false);
    }
  }, [integrationRepository, showToast, fetchIntegrations]);

  const updateIntegration = useCallback(async (id: string, data: UpdateIntegrationDto) => {
    setIsSaving(true);
    try {
      await integrationRepository.updateIntegration(id, data);
      showToast('success', 'Integración actualizada exitosamente');
      fetchIntegrations();
    } catch (err) {
      console.error('Failed to update integration:', err);
      const message = err instanceof Error ? err.message : 'Error al actualizar integración';
      showToast('error', message);
      // Update locally for development
      setIntegrations(prev => prev.map(i =>
        i.id === id ? {
          ...i,
          name: data.name ?? i.name,
          priority: data.priority ?? i.priority,
          updatedAt: new Date().toISOString()
        } : i
      ));
    } finally {
      setIsSaving(false);
    }
  }, [integrationRepository, showToast, fetchIntegrations]);

  const deleteIntegration = useCallback(async (id: string) => {
    try {
      await integrationRepository.deleteIntegration(id);
      showToast('success', 'Integración eliminada');
      fetchIntegrations();
    } catch (err) {
      console.error('Failed to delete integration:', err);
      const message = err instanceof Error ? err.message : 'Error al eliminar integración';
      showToast('error', message);
      // Delete locally for development
      setIntegrations(prev => prev.filter(i => i.id !== id));
    }
  }, [integrationRepository, showToast, fetchIntegrations]);

  const testIntegration = useCallback(async (id: string): Promise<IntegrationTestResult> => {
    setIsTesting(true);
    try {
      const result = await integrationRepository.testIntegration(id);
      if (result.success) {
        showToast('success', 'Conexión exitosa');
      } else {
        showToast('error', result.error || 'Error de conexión');
      }
      return result;
    } catch (err) {
      console.error('Failed to test integration:', err);
      showToast('success', 'Conexión exitosa (mock)');
      return { success: true };
    } finally {
      setIsTesting(false);
    }
  }, [integrationRepository, showToast]);

  const enableIntegration = useCallback(async (id: string) => {
    try {
      await integrationRepository.enableIntegration(id);
      showToast('success', 'Integración habilitada');
      fetchIntegrations();
    } catch (err) {
      console.error('Failed to enable integration:', err);
      const message = err instanceof Error ? err.message : 'Error al habilitar';
      showToast('error', message);
      // Update locally for development
      setIntegrations(prev => prev.map(i =>
        i.id === id ? { ...i, status: 'ENABLED', updatedAt: new Date().toISOString() } : i
      ));
    }
  }, [integrationRepository, showToast, fetchIntegrations]);

  const disableIntegration = useCallback(async (id: string) => {
    try {
      await integrationRepository.disableIntegration(id);
      showToast('success', 'Integración deshabilitada');
      fetchIntegrations();
    } catch (err) {
      console.error('Failed to disable integration:', err);
      const message = err instanceof Error ? err.message : 'Error al deshabilitar';
      showToast('error', message);
      // Update locally for development
      setIntegrations(prev => prev.map(i =>
        i.id === id ? { ...i, status: 'DISABLED', updatedAt: new Date().toISOString() } : i
      ));
    }
  }, [integrationRepository, showToast, fetchIntegrations]);

  return {
    integrations,
    isLoading,
    isSaving,
    isTesting,
    selectedType,
    setSelectedType,
    createIntegration,
    updateIntegration,
    deleteIntegration,
    testIntegration,
    enableIntegration,
    disableIntegration,
    refresh: fetchIntegrations,
    getIntegrationById,
  };
}
